/**
 * The bake step of a default save (libs/bakedBg.ts).
 *
 * When a save carries the crop, every banner on the page that shows a
 * positioned image is cut to its background box, and the cut images go to
 * the row of the displayed background. A cut takes seconds, so the cuts are
 * made after every other write of the save, and their write comes last. A
 * banner whose cut fails, or whose cut the save did not finish (the page was
 * left), keeps its crop; a failed cut is reported. The save goes on, because
 * a banner without a baked image is still correct.
 *
 * The background row then carries, per baked banner, the image, its meta, and
 * the crop it was cut from. The crop is what code that does not know baked
 * images reads, and what the cascade compares the meta with.
 */
import {
  BAKED_BG_CHANNELS,
  BAKED_BGS_FIELD,
  BAKED_BG_META_FIELD,
  type BakedBgMeta,
  type BakedBgTransform,
} from "libs/bakedBg";

export {BAKED_BG_CHANNELS};

export interface BakeSource {
  src: string;
  width: number;
  height: number;
  transform: BakedBgTransform;
}

export interface BakeJob extends BakeSource {
  id: string;
}

export interface BakedResult {
  url: string;
  meta: BakedBgMeta;
}

export type BakeRequest = (
  source: BakeSource
) => Promise<{url: string; pixelRatio: number}>;

const UNBAKEABLE = /^(blob:|data:image\/svg)|\.(svg|mp4|webm|ogv|mov|m4v|mkv)(\?|#|$)/i;

/** Can the bake cut this image? Not a video, an SVG or a local upload. */
export function isBakeableSource(src: string | null | undefined): boolean {
  return Boolean(src) && !UNBAKEABLE.test(src!);
}

/**
 * The banners to cut: each template on the page whose BgTransformer reports a
 * source with a raster image and a real box. A banner that still shows its
 * cut's crop has no source, and a ready cut registers none.
 */
export function collectBakeJobs(
  templateIds: readonly string[],
  getOps: (id: string) => {bakeSource?: () => BakeSource | null} | null
): BakeJob[] {
  const jobs: BakeJob[] = [];
  for (const id of templateIds) {
    const source = getOps(id)?.bakeSource?.();
    if (!source || !isBakeableSource(source.src)) continue;
    if (!(source.width > 0) || !(source.height > 0)) continue;
    jobs.push({id, ...source});
  }
  return jobs;
}

export interface BakeRun {
  results: Record<string, BakedResult>;
  /** Banners whose cut failed: they keep their crop. */
  failed: string[];
}

/** Cut every job, a few at a time. A failed cut is recorded, not thrown. */
export async function runBakeJobs(
  jobs: readonly BakeJob[],
  bake: BakeRequest,
  concurrency = 3
): Promise<BakeRun> {
  const results: Record<string, BakedResult> = {};
  const failed: string[] = [];
  let next = 0;
  const worker = async () => {
    while (next < jobs.length) {
      const job = jobs[next++]!;
      let url: string;
      let pixelRatio: number;
      try {
        ({url, pixelRatio} = await bake({
          src: job.src,
          width: job.width,
          height: job.height,
          transform: job.transform,
        }));
        if (!url) throw new Error("no url");
      } catch (err) {
        console.warn(`[DefaultScenePanel] Bake failed for ${job.id}:`, err);
        failed.push(job.id);
        continue;
      }
      results[job.id] = {
        url,
        meta: {
          src: job.src,
          ...job.transform,
          width: job.width,
          height: job.height,
          pixelRatio,
        },
      };
    }
  };
  await Promise.all(Array.from({length: Math.min(concurrency, jobs.length)}, worker));
  return {results, failed: failed.sort()};
}

const CROP_RECORDS = [
  ["bgX", "x"],
  ["bgY", "y"],
  ["bgScale", "scale"],
  ["bgRotate", "rotate"],
  ["bgFlip", "flip"],
] as const;

/**
 * The fields to write on the background row: each record the row has after
 * the save's other writes (`base`), with the baked banners added. Every field
 * is a whole record, because a PATCH replaces each field it names.
 */
export function bakeRowPatch(
  base: Record<string, any> | null | undefined,
  results: Record<string, BakedResult>
): Record<string, Record<string, unknown>> {
  const patch: Record<string, Record<string, unknown>> = {
    [BAKED_BGS_FIELD]: {...(base?.[BAKED_BGS_FIELD] ?? {})},
    [BAKED_BG_META_FIELD]: {...(base?.[BAKED_BG_META_FIELD] ?? {})},
  };
  for (const [field] of CROP_RECORDS) patch[field] = {...(base?.[field] ?? {})};
  for (const [id, {url, meta}] of Object.entries(results)) {
    patch[BAKED_BGS_FIELD]![id] = url;
    patch[BAKED_BG_META_FIELD]![id] = meta;
    for (const [field, key] of CROP_RECORDS) patch[field]![id] = meta[key];
  }
  return patch;
}

export interface RowAddress {
  layout: string | null;
  background: string | null;
  variant: string | null;
}

/**
 * The row the baked images go to. A baked image is cut from one image, so it
 * goes to a row of the displayed background, never to one that applies to
 * every background:
 *   - the crop's own row, or the chosen row, when it is scoped to one;
 *   - for a crop on a layout row, the (layout, background) row;
 *   - else the plain row of the displayed background.
 */
export function bakeRowAddress({
  cropTarget,
  chosen,
  bgOption,
}: {
  cropTarget: RowAddress | null | undefined;
  chosen: RowAddress;
  bgOption: string;
}): RowAddress {
  if (cropTarget?.background) return cropTarget;
  if (!cropTarget && chosen.background) return chosen;
  const layout = (cropTarget ?? chosen).layout;
  return {layout: layout || null, background: bgOption, variant: null};
}
